/* eslint-disable no-underscore-dangle */
import React from 'react';

import { AutocompleteProps } from '@lightspeed/flame/Autocomplete';
import { IconDomains } from '@lightspeed/flame/Icon/Domains';
import axios from 'axios';

import { createReactRenderer } from 'helpers/react-renderer';
import { sortCaseInsensitive } from 'pages/utils/sort';

import Autocomplete from '..';
import GuiDefHiddenInput from '../GuiDefHiddenInput';
import { customFilter, getIsViewPage, useCallbackState } from '../utils';
import {
  hideGuiDefsElements,
  runOnChangeCallbackFunc,
  runOnChangeCallbackFuncAsString,
} from './utils';

export const ALL = { value: -1, label: 'All Vendors', translate: true };
export const NONE = { value: 0, label: 'None', translate: true };

const addNuorderLinkedIcon = (option: any) => ({
  ...option,
  showIcon: !!option.b2bSellerUID,
});

const VendorsAutocomplete: React.FC<AutocompleteProps> = ({
  hasNone = false,
  hasAll = false,
  preventOptionsLoadBeforeFocus = false,
  defaultValue,
  defaultOptions = [],
  placeholder = 'Select or Enter a Vendor',
  hiddenInput,
  hideElementsOnChange = [],
  onCreateOption,
  onChange,
  onChangeCallback,
  ...rest
}) => {
  const [value, setValue] = useCallbackState(defaultValue);
  const [options, setOptions] = React.useState(defaultOptions);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isInitialFocus, setIsInitialFocus] = React.useState(preventOptionsLoadBeforeFocus);
  const [isError, setIsError] = React.useState(false);

  React.useEffect(() => {
    if (!defaultValue) {
      if (hasAll) {
        setValue(addNuorderLinkedIcon(ALL));
      } else if (hasNone) {
        setValue(addNuorderLinkedIcon(NONE));
      }
    } else if (parseInt(defaultValue?.value, 10) === -1) {
      setValue(addNuorderLinkedIcon(ALL));
    } else if (parseInt(defaultValue?.value, 10) === 0) {
      setValue(addNuorderLinkedIcon(NONE));
    } else {
      setValue(addNuorderLinkedIcon(defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (isInitialFocus || options.length) return;

    if (document.getElementById('listing_all_vendors')) {
      try {
        setOptions(
          JSON.parse(
            document.getElementById('listing_all_vendors')?.getAttribute('value') as string
          )
        );
      } catch (err) {
        // Silence
      }
    } else {
      setIsLoading(true);
      // @ts-ignore
      axios.get('/admin/vendors/all').then(async ({ data: { data } } = {}) => {
        const { vendors = [] } = (data || {}) as {
          vendors: { vendorID: string; name: string; b2bSellerUID: string }[];
        };

        const allVendors = vendors.map(({ vendorID, name, b2bSellerUID }) => ({
          value: vendorID,
          label: name,
          name,
          showIcon: !!b2bSellerUID,
          b2bSellerUID,
        }));

        const isVendorArchived = defaultValue
          ? !allVendors.some(
              ({ value: defaultOptionValue }) => defaultOptionValue === defaultValue.value
            )
          : false;

        if (isVendorArchived) {
          try {
            const response = await axios.get(`/admin/vendors/${defaultValue.value}`);
            if (response.status === 200) {
              allVendors.push({
                ...defaultValue,
                name: defaultValue.label,
                showIcon: !!defaultValue.b2bSellerUID,
                b2bSellerUID: defaultValue.b2bSellerUID,
              });
            }
          } catch (err) {
            // Invalid vendor
          }
        }

        const newOptions = sortOptions(allVendors);

        setOptions(newOptions);

        // Enrich value with showIcon/b2bSellerUID if possible
        if (value && value.value) {
          const matched = newOptions.find((opt: { value: any }) => opt.value === value.value);
          if (matched) setValue(matched);
        }

        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialFocus]);

  const handleChange = (newValue: AutocompleteOption) => {
    setValue(newValue === null ? '' : addNuorderLinkedIcon(newValue), () => {
      if (typeof onChange === 'function') {
        onChange(newValue);
      } else if (typeof onChange === 'string') {
        runOnChangeCallbackFuncAsString(onChange);
      }
    });

    if (hideElementsOnChange.length) hideGuiDefsElements(hideElementsOnChange);
    if (onChangeCallback) {
      runOnChangeCallbackFunc(onChangeCallback, newValue?.value);
    }
  };

  const sortOptions = React.useCallback(
    (newValues) => {
      return newValues
        .concat(options)
        .sort((a: AutocompleteOption, b: AutocompleteOption) =>
          sortCaseInsensitive(a.label, b.label)
        );
    },
    [options]
  );

  const handleOnCreateOption = async (label: string) => {
    setIsLoading(true);

    try {
      const response = await axios.post(`/admin/vendors`, { vendor: { name: label } });
      const { data: { vendors: { vendorID = null } = {} } = {} } = response.data;
      const newValue = { value: vendorID, label, name: label };

      setOptions(sortOptions([newValue]));
      handleChange(newValue);

      if (getIsViewPage()) {
        window.activateViewOnChangeButtons('view');
      }
    } catch (err) {
      const message =
        err.response?.data?.errors?.[0]?.detail ||
        err.response?.data?.message ||
        'There was an error while adding the vendor';
      window.alertUser(message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const { value: hiddenInputValue = '' } = value || {};

  return (
    <>
      <Autocomplete
        aria-label="vendors-autocomplete-input"
        {...rest}
        value={value}
        options={(
          [
            hasAll ? addNuorderLinkedIcon(ALL) : undefined,
            hasNone ? addNuorderLinkedIcon(NONE) : undefined,
          ] as AutocompleteOption[]
        )
          .filter(Boolean)
          .concat(options)}
        filterOption={(
          option: AutocompleteOption & { data: { __isNew__: boolean } },
          inputValue: string
        ) => {
          if (option.data.__isNew__) return true;
          return customFilter(option, inputValue);
        }}
        onFocus={() => {
          setIsInitialFocus(false);
        }}
        placeholder={!rest.isDisabled && placeholder}
        isLoading={isLoading}
        onChange={handleChange}
        onCreateOption={onCreateOption || handleOnCreateOption}
        hasError={isError}
        {...(isError ? { onInputChange: () => setIsError(false) } : {})}
        formatOptionLabel={(option: any) => ({
          label: (
            <>
              {option.showIcon && (
                <span style={{ marginRight: '4px' }}>
                  <IconDomains />
                </span>
              )}
              {option.label}
            </>
          ),
          translate: option.translate,
          __isNew__: option.__isNew__,
        })}
      />

      {hiddenInput ? <GuiDefHiddenInput {...hiddenInput} defaultValue={hiddenInputValue} /> : null}
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: VendorsAutocomplete,
  selector: '[data-react-root="VendorsAutocomplete"]',
});

history.onChangeState(
  [
    {
      name: [
        'item.views.item',
        'item.views.register_item',
        'item.views.quick_add',
        'item.views.register_workorder_item',
        'purchase.views.purchase',
        'item_group.views.item_group',
        'transfer.views.transfer',
        'vendor_return.views.vendor_return',
        'purchase.views.master_order',
        'item.listings.quick_edit_items',
      ],
      id: undefined,
    },
  ],
  () => {
    RenderedComponent.mount();
  },
  () => {
    RenderedComponent.unmount();
  }
);

export default VendorsAutocomplete;
